import { SpecialCase } from '../../../interfaces';
import { GuidStrValue } from '../../../helpers';
import { AttachmentExtension } from '../../webapi/Attachments/AttachmentExtension';
import { AttachmentDetailsDto, ResultDetailsDto } from '../../webapi/FND/Results/FndResultDetailDto';
import { ResultAbnormalFlagEnum } from '../../webapi/FND/Results/FNDResultsCategoryDto';

export enum LabResultSortType {
    Date = 'Date',
    Attachment = 'Attachment',
    Template = 'Template',
    Results = 'Results',
    Flowsheet = 'Flowsheet',
}

export interface PatientLabSearchRequest {
    Group: LabSearchGroupDto;
    TestValues: LabSearchTestValuesDto;
    Medication: LabSearchMedication;
    Date: LabSearchDateDto;
    MaxRows: number;
    IncludeCancelled: boolean;
    IncludeStrikeOuts: boolean;
    IsDefaultFilter: boolean;
}

export interface LabSearchGroupDto {
    SortBy: LabResultSortType;
    TemplateDetails: LabTemplateDetailsDto[];
}

export interface LabSearchDateDto {
    DateTypeUid: GuidStrValue | null;
    RangeUid: GuidStrValue | null;
    StartDate: Date | null;
    EndDate: Date | null;
}

export interface LabSearchMedication {
    ShowMedicationsOnFlowSheet: boolean;
    GenericDrugDetails: GenericDrugDetailsDto[];
}

export interface LabSearchTestValuesDto {
    ShowTestItems: boolean;
    MergeTestItemsByMappingOrLOINC: boolean;
    MergeLabTemplatesByLOINC: boolean;
}

export interface LabResultMedicationDto {
    DrugName: string;
    DrugDetailText: string;
    DrugUid: GuidStrValue;
}

export interface LabTemplateDetailsDto {
    TemplateName: string;
    TemplateUid: GuidStrValue;
}

export interface GenericDrugDetailsDto {
    GenericDrugName: string;
    GenericDrugNameUid: GuidStrValue;
    GenericDrugNameID: number;
}

export interface LabResultTestItemDto {
    Name: string;
    Value: string;
    IsAbnormal: boolean;
    ObservationItemUid: GuidStrValue;
    SpecialCase: SpecialCase;
}

export interface LabResultRecordDto {
    DateText: string;
    Date?: string;
    Status: string;
    ProviderName: string;
    TechnicianName: string;
    Type: string;
    CPT: string;
    OrderType: string;
    LaboratoryName: string;
    Description: string;
    AttachmentName: string;
    TemplateName: string;
    ResultName: string;
    IsResultAbnormal: boolean;
    TestResultItems: LabResultTestItemDto[];
    Medications: LabResultMedicationDto[];
    Comment: string;
    LOINCUid: GuidStrValue;
    TemplateUid: GuidStrValue;
    DisplayDate: string;
    DisplayName: string;
    Category: string;
    ResultUid: GuidStrValue;
    ObservationItemUid: GuidStrValue;
    AttachmentDetails: AttachmentDetailsDto[];
}

export interface LabResultRecordItemDto {
    CanPrintDocument: boolean;
    CanSendDocument: boolean;
    ResultUid: GuidStrValue;
    Name: string;
    IsProcedure: boolean;
    ResultType: PatientResultType;
}

export interface LabResultRecordGroupDto {
    HeaderText: string;
    Items: LabResultRecordItemDto[];
    Records: LabResultRecordDto[];
    ResultType: string;
}

export interface ExistingResultEventDto {
  AttachmentTypeUid: GuidStrValue;
  Description: string;
  IsDelete: number;
  MessageUrgencyUid: GuidStrValue;
  ObservationOrderResultStatusUid: GuidStrValue;
  OrderDateTime: string;
  PatientUid: GuidStrValue;
  Pk: GuidStrValue;
  ProviderName: string;
  ProviderUid: GuidStrValue;
  RPNResultUid: GuidStrValue;
  RelAttachmentGroupUid: GuidStrValue;
  ResultUid: GuidStrValue;
  ResultingVisitUid: GuidStrValue;
  Selected: boolean;
  Status: string;
  VisitProcedureName: string;
};


export interface PatientResultAttachmentDetailDto {
  AttachmentUid: GuidStrValue;
  Name: string;
  AttachmentTypeUid: GuidStrValue;
  Notes: string;
  IncludeInSummary: boolean;
  FileName: string;
  LastModifiedDate?: string | null;
  EffectiveDate?: string | null;
  AttachmentContent: AttachmentContentsDto;
  AttachmentGroupUid: GuidStrValue;
  RelAttachmentGroupUid: GuidStrValue;
}

export interface AttachmentContentsDto {
  Data: string;
  MimeType: string | null;
  fileName: string;
  Extension: AttachmentExtension;
}



export enum PatientResultType {
    Attachment = 'Attachment',
    LabResult = 'LabResult'
}




export interface TrackPatientResultDto {
  ObservationOrderUid: GuidStrValue;
  UrgentText: string | null;
  IsUrgent: boolean;
  AbnormalFlag: string;
  IsAbnormal: boolean;
  IsStruckOut: boolean;
  Status: string;
  PhaseName: string | null;
  OrderedDate?: Date | string | null;
  OrderedDateText: string;
  DueDate?: Date | string | null;
  DueDateText: string;
  ResultedDate?: Date | string | null;
  ResultedDateText: string | null;
  ProviderName: string;
  AttachmentType: string;
  CPT: string;
  OrderType: string;
  LaboratoryName: string;
  PSC: boolean;
  Description: string;
  DxAssociation: string;
  ResultUid: GuidStrValue;
  RPNResultTypeID: number;
  ReferredByProviderName: string;
  ServiceSiteName: string;
  PatientCity: string | null;
  PatientState: string | null;
  PatientZip: string | null;
  AccessionNumber: string | null;
  UrgencyText: string;
  Template: string;
}

export interface OrderResultStatusHistoryDto {
    PatientUid: GuidStrValue;
    VisitUid: GuidStrValue;
    ObservationOrderUid: GuidStrValue;
    ProcedureDescription: string;
    StatusHistory: ViewOrderResultStatusHistoryDto[];
    ObservationOrders: OrderResultObservationOrderDto[];
    HasObservationOrder: boolean;
    HasOrderResultStatusHistory: boolean;
    PatientFirstName: string;
    PatientLastName: string;
}

export interface OrderResultObservationOrderDto {
    ApproveDate: Date;
    ProcedureCode: string;
    AbnormalFlag: ResultAbnormalFlagEnum;
    IsAbnormal: boolean;
    ResultStatus: string;
    Status: string;
    ResultDate: Date;
    ResultDateText: string;
    LaboratoryName: string;
    OrderingProviderName: string;
    AccessionNumber: string;
    ObservationValues: ResultDetailsDto[];
    MissingObservationValues: ResultDetailsDto[];

}

export interface ViewOrderResultStatusHistoryDto {
    HistoryLastModifiedDate: Date;
    ResultStatusName: string;
    ModifyPersonName: string;
}
